import type { ContentPage } from "@/lib/content/types";

// CRM › Process Management hub.
export const processManagement: ContentPage = {
  slug: "process-management",
  template: "hrmsFeature",
  seo: {
    title: "Process Management Software - Standardize Your Sales Process | GetBiz CRM",
    description:
      "Design, enforce and automate your sales process with GetBiz CRM process management — blueprints, lead scoring, approvals, assignment and validation rules in one place.",
    keywords: [
      "process management software",
      "sales process automation",
      "sales blueprint",
      "approval process software",
      "lead scoring",
      "workflow rules",
      "CRM process management",
    ],
    ogImage: "/assets/data/seoimages/process-management.webp",
  },
  leaf: {
    hub: true,
    benefits: [
      { t: "One winning playbook", d: "Turn best practice into repeatable steps." },
      { t: "Guided selling", d: "Blueprints walk reps through each stage." },
      { t: "Faster approvals", d: "Route discounts and deals for sign-off in seconds." },
      { t: "Right owner, always", d: "Assignment rules route every record." },
      { t: "Clean data", d: "Validation rules block bad input at the source." },
      { t: "Built-in QA", d: "Review steps catch issues before records progress." },
      { t: "Focus on best leads", d: "Scoring surfaces the highest-intent leads." },
      { t: "Consistent results", d: "Every rep follows the same proven process." },
    ],
    process: {
      title: "How Process Management Works, Step by Step",
      description: "Turn your winning sales process into automated, enforced rules from first stage to close.",
      steps: [
        { title: "Design the Blueprint", caption: "Map every stage and the steps reps must follow.", image: "/assets/images/img/product/crm/design-the-blueprint.webp" },
        { title: "Score & Assign", caption: "Rank leads and route each record to the right owner.", image: "/assets/images/img/product/crm/score-and-assign.webp" },
        { title: "Guide the Rep", caption: "Blueprints guide reps through the right actions.", image: "/assets/images/img/product/crm/guide-the-rep.webp" },
        { title: "Review & Approve", caption: "Route approvals and QA checks automatically.", image: "/assets/images/img/product/crm/reviewand-approve.webp" },
        { title: "Enforce & Improve", caption: "Validation keeps data clean as you refine.", image: "/assets/images/img/product/crm/enforce-and-improve.webp" },
      ],
    },
    highlights: {
      title: "Key Highlights of Process Management",
      items: [
        { image: "/assets/images/img/product/crm/guid-every-blueprints.webp", title: "Guide every rep with blueprints.", href: "/crm-software/process-management/sales-blueprint/", description: "Design a step-by-step process that guides reps through every stage so the winning playbook runs every time.", checklist: ["Guided stages","Step rules","Rep guidance","Repeatable"] },
        { image: "/assets/images/img/product/crm/focus-best-lead.webp", title: "Focus on the best leads.", href: "/crm-software/process-management/lead-scoring/", description: "Rank leads by intent and fit so reps always work the highest-value opportunities first.", checklist: ["Intent + fit","Auto-rank","Prioritize","Focus reps"] },
        { image: "/assets/images/img/product/crm/approve-deals.webp", title: "Approve deals in seconds.", href: "/crm-software/process-management/approval-process/", description: "Automate multi-level approvals for discounts and deals so nothing waits in an inbox.", checklist: ["Multi-level","Auto-route","Fast sign-off","Audit trail"] },
      ],
    },
    hero: {
      caption: "Process Management",
      title: "Standardize and Automate Your Entire Sales Process",
      description:
        "Turn your best-practice sales process into repeatable, automated rules — blueprints, scoring, approvals, assignment and validation — so every rep follows the winning playbook, every time.",
      h1: "caption",
    },
    split: {
      title: "Enforce Your Winning Process, Automatically",
      bullets: [
        { title: "Guided Blueprints", description: "Map every stage and guide reps through the right steps." },
        { title: "Automated Approvals", description: "Route discounts and deals for sign-off in seconds." },
        { title: "Smart Assignment", description: "Send every record to the right owner with rules." },
        { title: "Clean, Valid Data", description: "Enforce data quality with validation rules." },
      ],
      image: "/assets/images/img/product/crm/process-management.webp",
      imageAlt: "GetBiz CRM process management",
    },
    cardsHeading: {
      title: "Everything in Process Management",
      description:
        "All the tools to design, enforce and automate a consistent sales process.",
    },
    cards: [
      { title: "Sales Blueprint", href: "/crm-software/process-management/sales-blueprint/", description: "Design a step-by-step process that guides reps through every stage.", image: "/assets/images/img/product/crm/sales-blueprint.webp", imageAlt: "CRM sales blueprint", checklist: ["Guided Stages","Step Rules","Rep Guidance","Repeatable"] },
      { title: "Lead Scoring", href: "/crm-software/process-management/lead-scoring/", description: "Rank leads by intent and fit so reps focus on the best.", image: "/assets/images/img/product/crm/lead-scoring.webp", imageAlt: "CRM lead scoring", checklist: ["Intent + Fit","Auto-Rank","Prioritize","Focus Reps"] },
      { title: "Approval Process", href: "/crm-software/process-management/approval-process/", description: "Automate multi-level approvals for discounts, deals and more.", image: "/assets/images/img/product/crm/approval-process.webp", imageAlt: "CRM approval process", checklist: ["Multi-Level","Auto-Route","Fast Sign-off","Audit Trail"] },
      { title: "Assignment Rule", href: "/crm-software/process-management/assignment-rule/", description: "Auto-assign records to the right rep by any criteria.", image: "/assets/images/img/product/crm/assignment-rule.webp", imageAlt: "CRM assignment rule", checklist: ["Auto-Assign","Any Criteria","Right Owner","No Delays"] },
      { title: "Review Process", href: "/crm-software/process-management/review-process/", description: "Add structured review and QA steps before records progress.", image: "/assets/images/img/product/crm/review-process.webp", imageAlt: "CRM review process", checklist: ["QA Steps","Structured Review","Fewer Errors","Gatekeeping"] },
      { title: "Validation Rule", href: "/crm-software/process-management/validation-rule/", description: "Enforce data quality with rules that block bad input.", image: "/assets/images/img/product/crm/validation-rule.webp", imageAlt: "CRM validation rule", checklist: ["Block Bad Data","Enforce Format","Clean Records","Fewer Fixes"] },
      { title: "Multi-page Layout", href: "/crm-software/process-management/multi-page-layout/", description: "Tailor record layouts to each team, process and stage.", image: "/assets/images/img/product/crm/multi-page-layout.webp", imageAlt: "CRM multi-page layout", checklist: ["Per-Team Layouts","Stage-Based","Tailored Fields","Less Clutter"] },
    ],
    testimonials: {
      title: "Consistent processes, better results",
      description: "See why teams standardize their sales process with GetBiz CRM.",
    },
    bookDemoTitle: "Ready to standardize your sales process?",
    relatedBlogs: "slug",
    relatedBlogsKeyword: "what-is-sales-process-management, sales-process-optimization-guide, sales-process-mapping-guide",
    metaKey: "process-management",
  },
  sections: [],
};
